import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  Plus, Trash2, Send, Clock, CheckCircle2, Image as ImageIcon, Repeat, Users2, Loader2,
  Download, FileText, Upload, X, Images, Zap, AlertTriangle, CheckCheck, Check, XCircle, Gauge, MousePointerClick, Euro, Sparkles, GitBranch, MessageSquare, Ban, Save, RefreshCw, Smartphone,
} from "lucide-react";

const DAYS_IT = ["lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato", "domenica"];
const toLocalInput = (iso) => {
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fmtEur = (v, digits = 2) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: digits, maximumFractionDigits: 4 }).format(v || 0);
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useWorkspaces } from "@/lib/useWorkspaces";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import WhatsAppPreview from "@/components/WhatsAppPreview";
import { CampaignPreviewButton } from "@/components/CampaignPreviewButton";

const STATUS = {
  draft: { label: "Bozza", cls: "bg-slate-500/15 text-slate-400" },
  scheduled: { label: "Programmata", cls: "bg-[#F59E0B]/15 text-[#F59E0B]" },
  sending: { label: "In invio", cls: "bg-[#3B82F6]/15 text-[#3B82F6]" },
  sent: { label: "Inviata", cls: "bg-[#10B981]/15 text-[#10B981]" },
};

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [prefill, setPrefill] = useState(null);
  const { active } = useWorkspaces();
  const isDemo = Boolean(active?.demo);
  const location = useLocation();
  const [focusId, setFocusId] = useState(null);
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get("campagna")) { setFocusId(sp.get("campagna")); window.history.replaceState({}, "", "/campagne"); }
    if (sp.get("list")) {
      setPrefill({ list_ids: [sp.get("list")], name: sp.get("name") || "Campagna Clienti caldi", listName: sp.get("listName") || "Clienti caldi", count: Number(sp.get("count") || 0), kind: "hot" });
      window.history.replaceState({}, "", "/campagne");
    }
  }, [location.search]);
  const [clusters, setClusters] = useState([]);
  const [lists, setLists] = useState([]);
  const [templates, setTemplates] = useState([]);

  const load = async () => {
    const [c, cl, t, ls] = await Promise.all([
      api.get("/campaigns"), api.get("/clusters"), api.get("/templates"), api.get("/lists"),
    ]);
    setCampaigns(c.data);
    setClusters(cl.data);
    setTemplates(t.data);
    setLists(ls.data);
  };
  useEffect(() => { load(); const i = setInterval(load, 5000); return () => clearInterval(i); }, []);

  const remove = async (id) => {
    await api.delete(`/campaigns/${id}`);
    toast.success("Campagna eliminata");
    load();
  };

  const [updatingTpl, setUpdatingTpl] = useState(null);
  const applyApprovedTemplate = async (c) => {
    setUpdatingTpl(c.id);
    try {
      const { data } = await api.post(`/campaigns/${c.id}/use-approved-template`);
      toast.success(`Template aggiornato: ${data.from || "—"} → ${data.to}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Aggiornamento non riuscito");
    } finally { setUpdatingTpl(null); }
  };

  const exportReport = async (fmt) => {
    try {
      const res = await api.get(`/campaigns/export?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_campagne.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Report ${fmt.toUpperCase()} scaricato`);
    } catch (e) {
      toast.error("Errore durante l'esportazione");
    }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-100">Campagne</h1>
          <p className="text-slate-400 text-sm mt-1">Invia volantini e offerte in modo massivo</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="export-csv-button" onClick={() => exportReport("csv")}
                  className="bg-[#1E293B] border-[#10B981]/20 text-[#10B981] hover:bg-[#334155]">
            <Download className="h-4 w-4 mr-2" /> CSV
          </Button>
          <Button variant="outline" data-testid="export-pdf-button" onClick={() => exportReport("pdf")}
                  className="bg-[#1E293B] border-[#10B981]/20 text-[#10B981] hover:bg-[#334155]">
            <FileText className="h-4 w-4 mr-2" /> PDF
          </Button>
          {!isDemo && <PilotDialog />}
          <CampaignDialog clusters={clusters} lists={lists} templates={templates} onDone={load} prefill={prefill} onPrefillConsumed={() => setPrefill(null)} isDemo={isDemo} />
        </div>
      </div>

      <div className="space-y-4">
        {campaigns.map((c) => {
          const st = STATUS[c.status] || STATUS.draft;
          return (
            <div key={c.id} data-testid={`campaign-row-${c.id}`} id={`campaign-${c.id}`}
                 ref={focusId === c.id ? (el) => el && setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 150) : undefined}
                 className={`bg-[#111827]/80 border rounded-2xl p-5 hover:border-[#10B981]/40 transition-all ${focusId === c.id ? "border-[#F59E0B] ring-2 ring-[#F59E0B]/40" : "border-[#1F2937]"}`}>
              <div className="flex items-start gap-4">
                {c.image_url ? (
                  <img src={c.image_url} alt="" className="h-16 w-16 rounded-xl object-cover border border-[#1F2937] hidden sm:block"
                       onError={(e) => (e.target.style.display = "none")} />
                ) : (
                  <div className="h-16 w-16 rounded-xl bg-[#1E293B] items-center justify-center hidden sm:flex">
                    <Send className="h-6 w-6 text-[#10B981]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-slate-100">{c.name}</p>
                    <Badge className={`${st.cls} border-0 text-xs`}>{st.label}</Badge>
                    {c.recurrence !== "none" && (
                      <Badge className="bg-[#8B5CF6]/15 text-[#8B5CF6] border-0 text-xs">
                        <Repeat className="h-3 w-3 mr-1" />{c.recurrence}
                      </Badge>
                    )}
                    {c.meta_template_name && (isDemo ? (
                      <Badge className="bg-amber-500/15 text-amber-400 border-0 text-xs" data-testid={`campaign-demo-badge-${c.id}`}>
                        <Zap className="h-3 w-3 mr-1" />DEMO
                      </Badge>
                    ) : (
                      <Badge className="bg-[#10B981]/15 text-[#10B981] border-0 text-xs" data-testid={`campaign-real-badge-${c.id}`}>
                        <Zap className="h-3 w-3 mr-1" />REALE
                      </Badge>
                    ))}
                  </div>
                  <p className="text-sm text-slate-400 mt-1 line-clamp-1">{c.message_body}</p>
                  {c.send_blocked && (
                    <div className="mt-2 rounded-lg border border-[#EF4444]/50 bg-[#EF4444]/10 px-3 py-2 text-xs text-[#FCA5A5] flex gap-2" data-testid={`campaign-send-blocked-${c.id}`}>
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                      <span><b>Invio bloccato.</b> {c.send_blocked}</span>
                    </div>
                  )}
                  {c.approved_template_name && (
                    <div className="mt-2 rounded-lg border border-[#10B981]/60 bg-[#ECFDF5] px-3 py-2 text-xs text-[#065F46] flex items-center gap-3 flex-wrap" data-testid={`campaign-template-update-${c.id}`}>
                      <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 shrink-0" />
                        <span>Meta ha approvato <b className="font-mono2">{c.approved_template_name}</b>: questa campagna usa ancora <i>{c.meta_template_name}</i>.</span></span>
                      <Button size="sm" onClick={() => applyApprovedTemplate(c)} disabled={updatingTpl === c.id} data-testid={`campaign-use-approved-${c.id}`}
                              className="h-7 bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold text-xs">
                        {updatingTpl === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Usa la versione approvata</>}
                      </Button>
                    </div>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 flex-wrap">
                    <span className="flex items-center gap-1"><Users2 className="h-3.5 w-3.5" />
                      {c.all_contacts ? "Tutti gli iscritti" : ([...(c.cluster_names || []), ...(c.list_names || [])].join(", ") || "—")}</span>
                    {c.scheduled_at && (
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />
                        {new Date(c.scheduled_at).toLocaleString("it-IT")}</span>
                    )}
                    {c.per_contact_timing && (
                      <span className="text-[#F59E0B] flex items-center gap-1" data-testid={`campaign-timing-${c.id}`} title="Orario per contatto">
                        <Clock className="h-3.5 w-3.5" />Orario per contatto{c.timing_deferred ? ` · ${c.timing_deferred} in attesa della loro ora` : c.timing_waves ? ` · ${c.timing_waves} ondate` : ""}
                      </span>
                    )}
                  </div>
                  {(c.status === "sent" || c.is_real_sent) && c.stats && (
                    <div className="flex items-center gap-4 mt-3 text-xs flex-wrap">
                      <span className="text-slate-400">Destinatari: <b className="text-slate-200">{c.stats.recipients}</b></span>
                      <span className="text-[#38BDF8]">Consegnati: {c.stats.delivered}{c.is_real_sent ? ` (${c.delivery_rate}%)` : ""}</span>
                      <span className="text-[#8B5CF6]">Letti: {c.stats.read}{c.is_real_sent ? ` (${c.read_rate}%)` : ""}</span>
                      <span className="text-[#EF4444]">Falliti: {c.stats.failed}</span>
                      {c.clicks > 0 && (
                        <span className="text-[#F59E0B] flex items-center gap-1" data-testid={`campaign-clicks-${c.id}`}>
                          <MousePointerClick className="h-3.5 w-3.5" />Clic: {c.clicks}{c.click_contacts ? ` · ${c.click_contacts} contatti (${c.click_rate}%)` : ""}
                        </span>
                      )}
                      {c.is_real_sent && <span className="text-[#10B981]">• dati reali da Meta</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {c.status === "sent" && <CheckCircle2 className="h-5 w-5 text-[#10B981]" />}
                  {c.status === "sending" && <Loader2 className="h-5 w-5 text-[#3B82F6] animate-spin" />}
                  <CampaignPreviewButton campaign={c} />
                  {["sent", "sending", "scheduled", "completed"].includes(c.status) && <CampaignReportButton campaign={c} onRecontact={async (lst, kind, extra) => { await load(); setPrefill({ list_ids: [lst.id], name: `${kind === "undelivered" ? "Reinvio SMS" : kind === "not_clicked" ? "Secondo invio" : kind === "tapped" ? `Ricontatto «${extra}»` : "Ricontatto"} · ${c.name}`.slice(0, 80), listName: lst.name, count: lst.contact_count, kind, buttonText: extra, ...(kind === "undelivered" ? { channel: "sms", sms_text: (c.sms_text || c.message_body || "").slice(0, 320) } : {}) }); }} />}
                  {(c.resend_enabled || c.resend_of || c.resend_campaign_id) && <ChainButton campaign={c} />}
                  <button onClick={() => remove(c.id)} data-testid={`delete-campaign-${c.id}`}
                          className="text-slate-500 hover:text-[#EF4444]">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {campaigns.length === 0 && (
          <p className="text-slate-500 text-center py-10">Nessuna campagna. Creane una nuova.</p>
        )}
      </div>
    </div>
  );
}

function PilotDialog() {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState("");
  const [vars, setVars] = useState([]);
  const [phones, setPhones] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (open) {
      api.get("/whatsapp/live-templates")
        .then((r) => { const a = r.data.filter((t) => t.status === "APPROVED"); setTemplates(a); if (a[0]) { setTpl(`${a[0].name}|${a[0].language}`); setVars(Array.from({ length: a[0].variables || 0 }, () => "")); } })
        .catch(() => setTemplates([]));
    }
  }, [open]);

  const run = async () => {
    const list = phones.split(/[\n,;]+/).map((p) => p.trim()).filter(Boolean).slice(0, 5);
    if (!tpl) { toast.error("Seleziona un template approvato"); return; }
    if (list.length === 0) { toast.error("Inserisci almeno un numero"); return; }
    if (vars.some((v) => !v || !v.trim())) { toast.error("Compila tutte le variabili del template"); return; }
    setBusy(true); setResults([]);
    const [name, language] = tpl.split("|");
    const out = [];
    for (const to of list) {
      try {
        const { data } = await api.post("/whatsapp/test-send", { to, template_name: name, language, body_params: vars });
        out.push({ to, ok: true, wamid: data.wamid });
      } catch (err) {
        out.push({ to, ok: false, error: formatApiErrorDetail(err.response?.data?.detail) });
      }
      setResults([...out]);
    }
    setBusy(false);
    toast.success(`Pilota completato: ${out.filter((r) => r.ok).length}/${list.length} inviati`);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="pilot-button"
                className="bg-[#1E293B] border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#334155]">
          <Zap className="h-4 w-4 mr-2" /> Prova pilota reale
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Prova pilota reale</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-400">Invia subito un template approvato a pochi numeri (max 5) per validare tutto prima dei grandi volumi. Invio reale via WhatsApp.</p>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-sm">Template approvato</Label>
            <Select value={tpl} onValueChange={(v) => { setTpl(v); const t = templates.find((x) => `${x.name}|${x.language}` === v); setVars(Array.from({ length: (t && t.variables) || 0 }, () => "")); }}>
              <SelectTrigger className="bg-[#0A0E17] border-[#1F2937]" data-testid="pilot-template-select"><SelectValue placeholder="Scegli un template" /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                {templates.length === 0 && <SelectItem value="none" disabled>Nessun template approvato</SelectItem>}
                {templates.map((t) => <SelectItem key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>{t.name} ({t.language})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {vars.length > 0 && (
            <div className="space-y-1.5" data-testid="pilot-vars">
              <Label className="text-sm">Variabili del template</Label>
              {vars.map((v, i) => (
                <Input key={i} data-testid={`pilot-var-${i}`} value={v}
                       onChange={(e) => { const n = [...vars]; n[i] = e.target.value; setVars(n); }}
                       placeholder={`Variabile {{${i + 1}}}`} className="bg-[#0A0E17] border-[#1F2937]" />
              ))}
            </div>
          )}
          <div className="space-y-1.5"><Label className="text-sm">Numeri destinatari (uno per riga, con prefisso)</Label>
            <Textarea data-testid="pilot-phones" rows={3} value={phones} onChange={(e) => setPhones(e.target.value)}
                      className="bg-[#0A0E17] border-[#1F2937] resize-none" placeholder={"+39333...\n+39334..."} /></div>
          <Button onClick={run} disabled={busy} data-testid="pilot-run" className="w-full bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Invia pilota reale"}
          </Button>
          {results.length > 0 && (
            <div className="bg-[#0A0E17] border border-[#1F2937] rounded-xl p-3 space-y-1" data-testid="pilot-results">
              {results.map((r, i) => (
                <p key={i} className={`text-xs ${r.ok ? "text-[#10B981]" : "text-[#FCA5A5]"}`}>
                  {r.ok ? "✓" : "✗"} {r.to} {r.ok ? `· ${r.wamid.slice(0, 24)}…` : `· ${r.error}`}
                </p>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CampaignDialog({ clusters, lists, templates, onDone, prefill, onPrefillConsumed, isDemo = false }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recontact, setRecontact] = useState(null);
  const [form, setForm] = useState({
    name: "", type: "offer", message_body: "", image_url: "",
    template_id: null, variable_values: {}, cluster_ids: [], list_ids: [], all_contacts: false,
    meta_template_name: "", meta_template_language: "", meta_body_params: [], use_real: false,
    schedule_mode: "now", scheduled_at: "", recurrence: "none",
  });
  const imgRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [media, setMedia] = useState([]);
  const [metaTemplates, setMetaTemplates] = useState([]);
  const [publishedForms, setPublishedForms] = useState([]);
  const [formId, setFormId] = useState("");
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);
  const [aiBrief, setAiBrief] = useState("");
  const [aiTone, setAiTone] = useState("amichevole");
  const [aiBusy, setAiBusy] = useState(false);
  const [testResult, setTestResult] = useState(null); // {state, msg, wamid}
  const pollRef = useRef({ active: false });
  const [estimate, setEstimate] = useState(null); // {count, cost}
  const [bestTime, setBestTime] = useState(null); // suggestion from /reports/best-times
  const [bestApplied, setBestApplied] = useState(false);

  useEffect(() => () => { pollRef.current.active = false; }, []);

  useEffect(() => {
    if (!open) return;
    const hasAudience = form.all_contacts || form.cluster_ids.length > 0 || form.list_ids.length > 0;
    if (!hasAudience) { setEstimate(null); return; }
    const t = setTimeout(() => {
      api.post("/campaigns/estimate", {
        name: form.name || "stima", message_body: form.message_body || "-", template_id: form.template_id,
        meta_template_name: form.use_real ? form.meta_template_name || null : null,
        cluster_ids: form.cluster_ids, list_ids: form.list_ids, all_contacts: form.all_contacts,
        channel: form.channel || "whatsapp", sms_text: form.sms_text || "", sms_fallback: !!form.sms_fallback, pref_sms_policy: form.pref_sms_policy || "exclude",
      }).then((r) => setEstimate(r.data)).catch(() => setEstimate(null));
    }, 400);
    return () => clearTimeout(t);
  }, [open, form.all_contacts, form.cluster_ids, form.list_ids, form.template_id, form.meta_template_name, form.use_real, form.name, form.message_body, form.channel, form.sms_text, form.sms_fallback, form.pref_sms_policy]);

  useEffect(() => {
    if (!open) return;
    const start = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
    const params = { start };
    if (!form.all_contacts) {
      if (form.cluster_ids.length) params.cluster_ids = form.cluster_ids.join(",");
      if (form.list_ids.length) params.list_ids = form.list_ids.join(",");
    }
    const t = setTimeout(() => {
      api.get("/reports/best-times", { params }).then((r) => setBestTime(r.data.suggestion)).catch(() => setBestTime(null));
    }, 300);
    return () => clearTimeout(t);
  }, [open, form.all_contacts, form.cluster_ids, form.list_ids]);

  const applyBestTime = () => {
    if (!bestTime?.at) return;
    setForm((f) => ({ ...f, schedule_mode: "scheduled", scheduled_at: toLocalInput(bestTime.at) }));
    setBestApplied(true);
    toast.success(`Programmata per ${DAYS_IT[bestTime.day]} alle ${String(bestTime.hour).padStart(2, "0")}:00 (${bestTime.scope === "segment" ? "orario migliore dei destinatari scelti" : "orario migliore dal report"})`);
  };

  useEffect(() => {
    if (!prefill) return;
    setForm((f) => ({ ...f, name: prefill.name, list_ids: prefill.list_ids, cluster_ids: [], all_contacts: false,
      ...(prefill.channel ? { channel: prefill.channel, sms_text: prefill.sms_text || f.sms_text || "", use_real: false, meta_template_name: "" } : {}) }));
    setRecontact({ listName: prefill.listName, count: prefill.count, kind: prefill.kind, buttonText: prefill.buttonText });
    setOpen(true);
    onPrefillConsumed?.();
  }, [prefill, onPrefillConsumed]);

  const pollStatus = async (wamid) => {
    pollRef.current.active = true;
    for (let i = 0; i < 10 && pollRef.current.active; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      if (!pollRef.current.active) return;
      try {
        const { data } = await api.get(`/whatsapp/message-status/${wamid}`);
        const s = data.status;
        if (s === "read") { setTestResult({ state: "read", wamid }); return; }
        if (s === "delivered") { setTestResult({ state: "delivered", wamid }); }
        if (s === "failed") {
          const em = (data.errors && data.errors[0] && (data.errors[0].title || data.errors[0].message)) || "Consegna fallita";
          setTestResult({ state: "error", msg: em, wamid }); return;
        }
      } catch (e) { /* 404 finché il webhook non registra lo stato */ }
    }
  };

  const sendTest = async () => {
    if (!testPhone.trim()) { toast.error("Inserisci un numero per il test"); return; }
    if (!form.use_real && !form.message_body.trim()) { toast.error("Scrivi prima il messaggio"); return; }
    if (form.use_real && !form.meta_template_name) { toast.error("Seleziona un template Meta approvato"); return; }
    pollRef.current.active = false;
    setTestResult(null);
    setTesting(true);
    try {
      const { data } = await api.post("/campaigns/test", {
        phone: testPhone.trim(), message_body: form.message_body,
        variable_values: form.variable_values, image_url: form.image_url,
        meta_template_name: form.use_real ? form.meta_template_name : null,
        meta_template_language: form.use_real ? form.meta_template_language : null,
        meta_body_params: form.use_real ? (form.meta_body_params || []) : [],
      });
      if (data.real && data.wamid) {
        setTestResult({ state: "sent", wamid: data.wamid });
        toast.success("Messaggio di prova inviato su WhatsApp ✓");
        pollStatus(data.wamid);
      } else {
        setTestResult({ state: "demo" });
        toast.success("Messaggio di prova inviato (demo)");
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Invio non riuscito";
      setTestResult({ state: "error", msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    if (open) {
      api.get("/media").then((r) => setMedia(r.data)).catch(() => {});
      api.get("/whatsapp/live-templates")
        .then((r) => setMetaTemplates(r.data.filter((t) => t.status === "APPROVED")))
        .catch(() => setMetaTemplates([]));
      api.get("/forms/published").then((r) => setPublishedForms(r.data.items || [])).catch(() => setPublishedForms([]));
    }
  }, [open]);

  const selectedMeta = metaTemplates.find(
    (t) => t.name === form.meta_template_name && t.language === form.meta_template_language
  );
  const onSelectMeta = (val) => {
    const [name, language] = val.split("|");
    const t = metaTemplates.find((x) => x.name === name && x.language === language);
    setForm((f) => ({
      ...f, meta_template_name: name, meta_template_language: language,
      meta_body_params: Array(t ? t.variables : 0).fill(""),
      message_body: t ? t.body_text : f.message_body,
    }));
  };
  const setMetaParam = (i, v) => setForm((f) => {
    const arr = [...f.meta_body_params]; arr[i] = v; return { ...f, meta_body_params: arr };
  });

  const onImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Seleziona un file immagine");
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 2MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload/campaign-image", fd);
      setForm((prev) => ({ ...prev, image_url: data.url }));
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(err.response?.status === 403 ? formatApiErrorDetail(err.response?.data?.detail) : "Errore durante il caricamento dell'immagine");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const toggleCluster = (id) =>
    setForm((f) => ({
      ...f,
      cluster_ids: f.cluster_ids.includes(id) ? f.cluster_ids.filter((x) => x !== id) : [...f.cluster_ids, id],
    }));

  const toggleList = (id) =>
    setForm((f) => ({
      ...f,
      list_ids: f.list_ids.includes(id) ? f.list_ids.filter((x) => x !== id) : [...f.list_ids, id],
    }));

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    setForm((f) => ({ ...f, template_id: id, message_body: t ? t.body : f.message_body, variable_values: {} }));
  };

  const dynamicVars = Array.from(new Set(
    (form.message_body.match(/\{\{(\w+)\}\}/g) || []).map((m) => m.replace(/[{}]/g, ""))
  )).filter((v) => !["nome", "name"].includes(v));
  const setVarValue = (k, v) => setForm((f) => ({ ...f, variable_values: { ...f.variable_values, [k]: v } }));
  const fillVars = (text) => {
    let out = text || "";
    Object.entries(form.variable_values || {}).forEach(([k, v]) => { if (v) out = out.replaceAll(`{{${k}}}`, v); });
    return out;
  };

  const [confirmUntested, setConfirmUntested] = useState(false);
  const save = async (skipTestCheck = false) => {
    if (!form.name || !form.message_body) {
      toast.error("Nome e messaggio obbligatori");
      return;
    }
    if (!form.all_contacts && form.cluster_ids.length === 0 && form.list_ids.length === 0) {
      toast.error("Seleziona almeno un cluster, una lista o 'Tutti gli iscritti'");
      return;
    }
    if (form.schedule_mode === "scheduled" && !form.scheduled_at) {
      toast.error("Imposta data e ora di programmazione");
      return;
    }
    if (form.use_real && !form.meta_template_name) {
      toast.error("Seleziona un template Meta approvato per l'invio reale");
      return;
    }
    // Promemoria: invio reale a molti contatti senza aver mai provato il messaggio sul proprio numero
    if (!skipTestCheck && !isDemo && form.use_real && form.channel !== "sms" && !["sent", "delivered", "read"].includes(testResult?.state)) {
      setConfirmUntested(true);
      return;
    }
    setConfirmUntested(false);
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.use_real;
      if (form.channel === "sms") {
        if (!(form.sms_text || "").trim()) { toast.error("Scrivi il testo dell'SMS"); setSaving(false); return; }
        payload.meta_template_name = null;
        payload.meta_template_language = null;
        payload.meta_body_params = [];
      } else if (!form.use_real) {
        payload.meta_template_name = null;
        payload.meta_template_language = null;
        payload.meta_body_params = [];
      }
      if (payload.schedule_mode === "scheduled") {
        payload.scheduled_at = new Date(payload.scheduled_at).toISOString();
      } else {
        payload.scheduled_at = null;
      }
      payload.form_id = form.use_real && formId ? formId : null;
      await api.post("/campaigns", payload);
      toast.success(
        isDemo
          ? "Campagna demo salvata come bozza (nessun invio)"
          : form.schedule_mode === "scheduled"
          ? "Campagna programmata con successo"
          : form.use_real
            ? "Campagna in invio REALE via WhatsApp"
            : "Campagna in invio (modalità demo)"
      );
      setOpen(false);
      setRecontact(null);
      setForm({
        name: "", type: "offer", message_body: "", image_url: "",
        template_id: null, variable_values: {}, cluster_ids: [], list_ids: [], all_contacts: false,
        schedule_mode: "now", scheduled_at: "", recurrence: "none",
      });
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-campaign-button" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
          <Plus className="h-4 w-4 mr-2" /> Nuova campagna
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Nuova campagna massiva</DialogTitle></DialogHeader>
        {recontact && (
          <div className="rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-4 py-2.5 text-xs text-[#F59E0B] flex items-center gap-2" data-testid="recontact-banner">
            <MousePointerClick className="h-4 w-4 shrink-0" />
            <span>{recontact.kind === "undelivered" ? "Reinvio via SMS" : recontact.kind === "not_clicked" ? "Secondo volantino" : recontact.kind === "hot" ? "Campagna Clienti caldi" : recontact.kind === "tapped" ? "Ricontatto pulsante" : "Campagna di ricontatto"}: destinatari = lista <b>{recontact.listName}</b> ({recontact.count} contatti che {recontact.kind === "undelivered" ? "non hanno ricevuto il messaggio WhatsApp" : recontact.kind === "not_clicked" ? "hanno ricevuto ma non hanno cliccato" : recontact.kind === "hot" ? "hanno cliccato almeno 2 volte negli ultimi 90 giorni" : recontact.kind === "tapped" ? `hanno toccato il pulsante «${recontact.buttonText}»` : "hanno cliccato"}, aggiornata automaticamente). Scegli template o testo e invia.</span>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Form */}
          <div className="space-y-4">
            <div className="space-y-2"><Label>Nome campagna</Label>
              <Input data-testid="campaign-name-input" value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="bg-[#0A0E17] border-[#1F2937]" placeholder="Es. Volantino Settimanale" /></div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Tipo</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="bg-[#0A0E17] border-[#1F2937]" data-testid="campaign-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                    <SelectItem value="offer">Offerta</SelectItem>
                    <SelectItem value="flyer">Volantino</SelectItem>
                    <SelectItem value="text">Solo testo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Template</Label>
                <Select value={form.template_id || "none"} onValueChange={(v) => applyTemplate(v === "none" ? null : v)}>
                  <SelectTrigger className="bg-[#0A0E17] border-[#1F2937]" data-testid="campaign-template-select"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                    <SelectItem value="none">Nessuno</SelectItem>
                    {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2"><Label>Messaggio</Label>
              <div className="rounded-xl border border-[#A78BFA]/30 bg-[#A78BFA]/5 p-3 space-y-2" data-testid="campaign-ai-copy">
                <p className="text-xs text-[#C4B5FD] flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Genera il testo con Claude: descrivi l'offerta in poche parole</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input data-testid="campaign-ai-brief" value={aiBrief} onChange={(e) => setAiBrief(e.target.value)}
                         placeholder="Es. volantino weekend: -30% su frutta e verdura, valido sabato e domenica" className="bg-[#0A0E17] border-[#1F2937] flex-1" />
                  <Select value={aiTone} onValueChange={setAiTone}>
                    <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] sm:w-[150px]" data-testid="campaign-ai-tone"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                      {[["amichevole", "Amichevole"], ["professionale", "Professionale"], ["urgente", "Urgente"], ["esclusivo", "Esclusivo"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button type="button" disabled={aiBusy || aiBrief.trim().length < 5} data-testid="campaign-ai-generate"
                          onClick={async () => { setAiBusy(true); try { const { data } = await api.post("/campaigns/generate-text", { brief: aiBrief, tone: aiTone }); setForm((f) => ({ ...f, message_body: data.text })); toast.success("Testo generato: controlla e modifica se serve"); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Generazione non riuscita"); } finally { setAiBusy(false); } }}
                          className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-semibold shrink-0">
                    {aiBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />} {aiBusy ? "Scrivo…" : "Genera"}
                  </Button>
                </div>
              </div>
              <Textarea data-testid="campaign-body-input" rows={4} value={form.message_body}
                        onChange={(e) => setForm({ ...form, message_body: e.target.value })}
                        className="bg-[#0A0E17] border-[#1F2937] resize-none"
                        placeholder="Ciao {{nome}}, scopri le offerte della settimana!" /></div>

            {dynamicVars.length > 0 && (
              <div className="space-y-2" data-testid="campaign-variables">
                <Label className="text-xs text-slate-500">Valori campi dinamici</Label>
                {dynamicVars.map((v) => (
                  <div key={v} className="flex items-center gap-2">
                    <span className="text-xs font-mono2 text-[#8B5CF6] w-28 shrink-0">{`{{${v}}}`}</span>
                    <Input data-testid={`campaign-var-${v}`} value={form.variable_values[v] || ""}
                           onChange={(e) => setVarValue(v, e.target.value)}
                           className="bg-[#0A0E17] border-[#1F2937] h-9 flex-1" placeholder={`Valore per ${v}`} />
                  </div>
                ))}
                <p className="text-xs text-slate-500">{`{{nome}}`} viene compilato automaticamente per ogni contatto.</p>
              </div>
            )}

            {/* Canale di invio */}
            {isDemo ? (
              <div className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-300 rounded-xl px-3 py-2.5" data-testid="campaign-demo-notice">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span><b>Ambiente dimostrativo META REVIEW</b> — operazione simulata: la campagna viene salvata come <b>bozza</b>. Invio reale WhatsApp, SMS, messaggio di prova e pilota sono disabilitati; nessun messaggio viene inviato. / <em>Demo environment: simulated operation, the campaign is saved as a draft and nothing is sent.</em></span>
              </div>
            ) : (
            <div className="space-y-2" data-testid="channel-block">
              <Label>Canale di invio</Label>
              <div className="grid grid-cols-2 gap-2">
                {[["whatsapp", "WhatsApp"], ["sms", "SMS (SMSFactor)"]].map(([v, l]) => (
                  <button key={v} type="button" data-testid={`channel-${v}`} onClick={() => setForm({ ...form, channel: v, use_real: v === "sms" ? true : form.use_real })}
                          className={`px-3 py-2 rounded-lg text-sm border transition-all ${(form.channel || "whatsapp") === v ? "bg-[#10B981] text-[#0A0E17] border-[#10B981]" : "border-[#1F2937] text-slate-400"}`}>
                    {v === "sms" ? <MessageSquare className="h-4 w-4 inline mr-1" /> : <Send className="h-4 w-4 inline mr-1" />}{l}
                  </button>
                ))}
              </div>
              {(form.channel === "sms" || form.sms_fallback || form.pref_sms_policy === "send_sms") && (
                <div className="space-y-1" data-testid="sms-text-block">
                  <Label className="text-xs text-slate-400">Testo SMS {form.channel === "sms" ? "" : form.sms_fallback ? "(usato per il fallback)" : "(per chi preferisce SMS)"}</Label>
                  <Textarea value={form.sms_text || ""} onChange={(e) => setForm({ ...form, sms_text: e.target.value })} rows={3} data-testid="sms-text-input"
                            placeholder="Es. Ciao {{nome}}, scopri il volantino: https://..." className="bg-[#0A0E17] border-[#1F2937]" />
                  <p className="text-[11px] text-slate-500" data-testid="sms-segments">{(form.sms_text || "").length} caratteri · {Math.max(0, Math.ceil((form.sms_text || "").length / ((form.sms_text || "").length > 160 ? 153 : 160)))} SMS per destinatario · variabili {"{{nome}}"} ammesse · niente emoji (raddoppiano il costo)</p>
                </div>
              )}
              {form.channel !== "sms" && form.use_real && (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-[#1F2937] bg-[#0B1120] px-3 py-2" data-testid="sms-fallback-box">
                  <div>
                    <p className="text-xs font-medium text-slate-200">Fallback SMS</p>
                    <p className="text-[11px] text-slate-500">Se WhatsApp fallisce (numero senza WhatsApp), invia lo stesso contenuto via SMS.</p>
                  </div>
                  <Switch checked={!!form.sms_fallback} onCheckedChange={(v) => setForm({ ...form, sms_fallback: v })} data-testid="sms-fallback-switch" />
                </div>
              )}
              {form.channel !== "sms" && form.use_real && (
                <div className="rounded-lg border border-[#1F2937] bg-[#0B1120] px-3 py-2 space-y-2" data-testid="pref-sms-policy-box">
                  <div>
                    <p className="text-xs font-medium text-slate-200">Chi preferisce SMS</p>
                    <p className="text-[11px] text-slate-500">Contatti con canale preferito SMS in rubrica. I consensi e gli STOP vengono sempre rispettati, qualunque scelta.</p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {[["exclude", "Escludi"], ...(estimate?.force_whatsapp_enabled ? [["force_whatsapp", "Invia comunque via WhatsApp"]] : []), ["send_sms", "Invia SMS"]].map(([v, l]) => (
                      <button key={v} type="button" onClick={() => setForm({ ...form, pref_sms_policy: v, confirm_override: false })} data-testid={`pref-sms-policy-${v}`}
                              className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${(form.pref_sms_policy || "exclude") === v ? "bg-[#10B981]/15 border-[#10B981]/40 text-[#10B981]" : "border-[#1F2937] text-slate-400 hover:text-slate-200"}`}>{l}</button>
                    ))}
                  </div>
                  {form.pref_sms_policy === "force_whatsapp" && (
                    <label className="flex items-start gap-2 text-[11px] text-[#B45309] cursor-pointer" data-testid="pref-sms-override-warning">
                      <input type="checkbox" checked={!!form.confirm_override} onChange={(e) => setForm({ ...form, confirm_override: e.target.checked })} className="mt-0.5 accent-[#F59E0B]" data-testid="pref-sms-confirm-override" />
                      Confermo: questa campagna ignora la preferenza SMS e invia via WhatsApp ai contatti con consenso valido. La scelta viene registrata nell'audit.
                    </label>
                  )}
                  {form.pref_sms_policy === "send_sms" && !(form.sms_text || "").trim() && <p className="text-[11px] text-[#EF4444]" data-testid="pref-sms-text-required">Scrivi il testo SMS qui sopra: senza testo la campagna non può essere creata.</p>}
                  {estimate && !estimate.force_whatsapp_enabled && <p className="text-[11px] text-slate-500" data-testid="pref-sms-force-disabled-note">La forzatura WhatsApp («Invia comunque via WhatsApp») non è abilitata per questa azienda: può attivarla solo Studio Nouvelle con evidenze sui consensi per canale.</p>}
                </div>
              )}
            </div>
            )}

            {/* Invio reale via template Meta */}
            {!isDemo && (
            <div className={`space-y-3 bg-[#0A0E17] border border-[#10B981]/30 rounded-xl p-3 ${form.channel === "sms" ? "hidden" : ""}`} data-testid="meta-real-block">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-[#10B981]"><Zap className="h-4 w-4" /> Invio reale via WhatsApp (template Meta)</Label>
                <Switch checked={form.use_real} data-testid="use-real-switch"
                        onCheckedChange={(v) => setForm({ ...form, use_real: v })} />
              </div>
              {form.use_real && (
                <>
                  {publishedForms.length > 0 && (
                    <div className="space-y-1" data-testid="campaign-form-section">
                      <label className="text-xs text-slate-400">FORM (WhatsApp Flow) da allegare al template</label>
                      <select value={formId} onChange={(e) => setFormId(e.target.value)} data-testid="campaign-form-select" className="w-full h-9 rounded border border-slate-300 bg-white text-sm text-slate-800 px-2">
                        <option value="">Nessun Form</option>{publishedForms.map((f) => <option key={f.id} value={f.id}>{f.name} · v{f.published_version}{f.mode === "mock" ? " · MOCK" : ""}</option>)}
                      </select>
                      {formId && <p className="text-[11px] text-slate-500" data-testid="campaign-form-hint">Il template approvato deve avere un pulsante <b>Flow</b>; ogni contatto riceve il Form una sola volta per campagna, nel rispetto di consensi e limiti.</p>}
                    </div>
                  )}
                  <div className="flex items-start gap-2 text-xs text-[#FCD34D] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg px-3 py-2">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>Questa campagna verrà inviata <b>realmente</b> ai contatti tramite Meta (costi reali). È possibile usare solo template <b>approvati</b>.</span>
                  </div>
                  {metaTemplates.length === 0 ? (
                    <p className="text-xs text-slate-500">Nessun template approvato disponibile (o WhatsApp non collegato).</p>
                  ) : (
                    <Select value={form.meta_template_name ? `${form.meta_template_name}|${form.meta_template_language}` : ""}
                            onValueChange={onSelectMeta}>
                      <SelectTrigger className="bg-[#111827] border-[#1F2937]" data-testid="meta-template-select">
                        <SelectValue placeholder="Scegli un template approvato" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                        {metaTemplates.map((t) => (
                          <SelectItem key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>
                            {t.name} ({t.language}) · {t.category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {selectedMeta && selectedMeta.variables > 0 && (
                    <div className="space-y-2" data-testid="meta-params">
                      <Label className="text-xs text-slate-500">Variabili del template ({selectedMeta.variables})</Label>
                      {Array.from({ length: selectedMeta.variables }).map((_, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-xs font-mono2 text-[#10B981] w-12 shrink-0">{`{{${i + 1}}}`}</span>
                          <Input data-testid={`meta-param-${i}`} value={form.meta_body_params[i] || ""}
                                 onChange={(e) => setMetaParam(i, e.target.value)}
                                 className="bg-[#111827] border-[#1F2937] h-9 flex-1" placeholder={`Valore {{${i + 1}}}`} />
                        </div>
                      ))}
                      <p className="text-xs text-slate-500">Scrivi <span className="font-mono2 text-[#8B5CF6]">{`{{nome}}`}</span> per inserire automaticamente il nome di ogni contatto.</p>
                    </div>
                  )}
                  {form.use_real && form.meta_template_name && (() => {
                    const lt = templates.find((t) => t.id === form.template_id);
                    const hasLinks = lt && ((lt.buttons || []).some((b) => b.type === "url") || (lt.carousel || []).some((c) => (c.buttons || []).some((b) => b.type === "url")));
                    if (!lt || !hasLinks) return null;
                    return (lt.link_tracking || "none") === "none" ? (
                      <p className="text-[11px] text-[#F59E0B] flex items-center gap-1" data-testid="campaign-no-tracking-warning"><AlertTriangle className="h-3 w-3 shrink-0" />I clic sui link di questo template <b>non saranno rilevati</b>: il tracciamento è spento. Attivalo nel template e richiedilo di nuovo a Meta prima di inviare, se vuoi le statistiche dei clic.</p>
                    ) : (
                      <p className="text-[11px] text-slate-500" data-testid="campaign-tracking-info">Tracciamento clic attivo ({lt.link_tracking === "contact" ? "per contatto" : "conteggio"}): funziona solo se il template approvato su Meta è stato richiesto <b>dopo</b> aver attivato il tracciamento.</p>
                    );
                  })()}
                </>
              )}
            </div>
            )}

            {form.use_real && form.meta_template_name && (
              <div className="rounded-xl border border-[#1F2937] bg-[#0B1120] p-3" data-testid="timing-box">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-slate-200 flex items-center gap-1.5"><Clock className="h-4 w-4 text-[#F59E0B]" /> Orario per contatto</p>
                    <p className="text-[11px] text-slate-500">Ogni cliente riceve il messaggio nella fascia oraria in cui clicca di più (ultimi 90 giorni). Chi non ha dati riceve subito; gli altri partono nella loro ora, entro la giornata dell'invio.</p>
                  </div>
                  <Switch checked={!!form.per_contact_timing} onCheckedChange={(v) => setForm({ ...form, per_contact_timing: v })} data-testid="per-contact-timing-switch" />
                </div>
              </div>
            )}

            {form.use_real && form.meta_template_name && (
              <div className="rounded-xl border border-[#1F2937] bg-[#0B1120] p-3 space-y-2" data-testid="resend-box">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-medium text-slate-200">Reinvio automatico a chi non clicca</p>
                    <p className="text-[11px] text-slate-500">Dopo i giorni indicati, chi ha ricevuto ma non ha cliccato il link riceve da solo un secondo volantino.</p>
                  </div>
                  <Switch checked={!!form.resend_enabled} onCheckedChange={(v) => setForm({ ...form, resend_enabled: v })} data-testid="resend-switch" />
                </div>
                {form.resend_enabled && (
                  <div className="grid sm:grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-[11px] text-slate-500">Dopo quanti giorni</Label>
                      <Input type="number" min="1" max="30" value={form.resend_days ?? 3} onChange={(e) => setForm({ ...form, resend_days: parseInt(e.target.value || "3", 10) })}
                             className="bg-[#111827] border-[#1F2937] h-9" data-testid="resend-days-input" /></div>
                    <div className="space-y-1"><Label className="text-[11px] text-slate-500">Template del secondo invio</Label>
                      <Select value={form.resend_meta_template_name ? `${form.resend_meta_template_name}|${form.resend_meta_template_language}` : "same"}
                              onValueChange={(v) => { if (v === "same") setForm({ ...form, resend_meta_template_name: null, resend_meta_template_language: null }); else { const [n, l] = v.split("|"); setForm({ ...form, resend_meta_template_name: n, resend_meta_template_language: l }); } }}>
                        <SelectTrigger className="bg-[#111827] border-[#1F2937] h-9" data-testid="resend-template-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                          <SelectItem value="same">Stesso template della campagna</SelectItem>
                          {metaTemplates.filter((t) => t.name !== form.meta_template_name).map((t) => (
                            <SelectItem key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>{t.name} ({t.language})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select></div>
                  </div>
                )}
                {form.resend_enabled && (
                  <div className="border-t border-[#1F2937] pt-2 space-y-2" data-testid="resend2-box">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-medium text-slate-200">Terzo promemoria</p>
                        <p className="text-[11px] text-slate-500">A chi non clicca neanche il secondo invio, dopo altri N giorni dal secondo invio.</p>
                      </div>
                      <Switch checked={!!form.resend2_enabled} onCheckedChange={(v) => setForm({ ...form, resend2_enabled: v })} data-testid="resend2-switch" />
                    </div>
                    {form.resend2_enabled && (
                      <div className="grid sm:grid-cols-2 gap-2">
                        <div className="space-y-1"><Label className="text-[11px] text-slate-500">Dopo quanti giorni dal secondo invio</Label>
                          <Input type="number" min="1" max="30" value={form.resend2_days ?? 3} onChange={(e) => setForm({ ...form, resend2_days: parseInt(e.target.value || "3", 10) })}
                                 className="bg-[#111827] border-[#1F2937] h-9" data-testid="resend2-days-input" /></div>
                        <div className="space-y-1"><Label className="text-[11px] text-slate-500">Template del terzo invio</Label>
                          <Select value={form.resend2_meta_template_name ? `${form.resend2_meta_template_name}|${form.resend2_meta_template_language}` : "same"}
                                  onValueChange={(v) => { if (v === "same") setForm({ ...form, resend2_meta_template_name: null, resend2_meta_template_language: null }); else { const [n, l] = v.split("|"); setForm({ ...form, resend2_meta_template_name: n, resend2_meta_template_language: l }); } }}>
                            <SelectTrigger className="bg-[#111827] border-[#1F2937] h-9" data-testid="resend2-template-select"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                              <SelectItem value="same">Stesso template del secondo invio</SelectItem>
                              {metaTemplates.map((t) => (
                                <SelectItem key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>{t.name} ({t.language})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select></div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2"><Label className="flex items-center gap-2"><ImageIcon className="h-4 w-4" /> Immagine volantino (opzionale)</Label>
              <input ref={imgRef} type="file" accept="image/*" hidden onChange={onImage} data-testid="campaign-image-file" />
              {form.image_url ? (
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <img src={form.image_url} alt="anteprima" className="h-16 w-16 rounded-lg object-cover border border-[#1F2937]" />
                    <button type="button" data-testid="campaign-image-clear"
                            onClick={() => setForm({ ...form, image_url: "" })}
                            className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-[#EF4444] flex items-center justify-center">
                      <X className="h-3 w-3 text-white" />
                    </button>
                  </div>
                  <Button type="button" variant="outline" data-testid="campaign-image-change"
                          onClick={() => imgRef.current?.click()}
                          className="bg-[#1E293B] border-[#1F2937] text-slate-300">
                    Cambia
                  </Button>
                </div>
              ) : (
                <Button type="button" variant="outline" data-testid="campaign-image-upload" disabled={uploading}
                        onClick={() => imgRef.current?.click()}
                        className="w-full bg-[#0A0E17] border-dashed border-[#1F2937] text-slate-400 hover:border-[#10B981]/40">
                  {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                  {uploading ? "Caricamento..." : "Carica immagine"}
                </Button>
              )}
              <Input data-testid="campaign-image-input" value={form.image_url.startsWith("data:") ? "" : form.image_url}
                     onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                     className="bg-[#0A0E17] border-[#1F2937] text-xs" placeholder="...oppure incolla un URL" />
              <button type="button" data-testid="gallery-toggle" onClick={() => setGalleryOpen((o) => !o)}
                      className="text-xs text-[#10B981] hover:underline flex items-center gap-1 mt-1">
                <Images className="h-3.5 w-3.5" /> {galleryOpen ? "Nascondi galleria" : "Scegli dalla galleria volantini"}
              </button>
              {galleryOpen && (
                <div className="grid grid-cols-4 gap-2 mt-2 max-h-40 overflow-y-auto" data-testid="media-gallery">
                  {media.length === 0 && <p className="text-xs text-slate-500 col-span-4">Nessuna immagine caricata finora.</p>}
                  {media.map((m) => (
                    <button key={m.id} type="button" data-testid={`gallery-item-${m.id}`}
                            onClick={() => { setForm((f) => ({ ...f, image_url: m.url })); setGalleryOpen(false); toast.success("Immagine selezionata"); }}
                            className={`rounded-lg overflow-hidden border ${form.image_url === m.url ? "border-[#10B981]" : "border-[#1F2937]"} hover:border-[#10B981]/60`}>
                      <img src={m.url} alt="" className="h-14 w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}</div>

            {/* Audience */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Destinatari</Label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Tutti gli iscritti</span>
                  <Switch checked={form.all_contacts} data-testid="all-contacts-switch"
                          onCheckedChange={(v) => setForm({ ...form, all_contacts: v })} />
                </div>
              </div>
              {!form.all_contacts && (
                <div className="flex flex-wrap gap-2" data-testid="cluster-select-dropdown">
                  {clusters.map((c) => (
                    <button key={c.id} type="button" onClick={() => toggleCluster(c.id)}
                            data-testid={`campaign-cluster-${c.id}`}
                            className={`px-3 py-1.5 rounded-lg text-xs border transition-all ${
                              form.cluster_ids.includes(c.id)
                                ? "bg-[#10B981] text-[#0A0E17] border-[#10B981]"
                                : "border-[#1F2937] text-slate-400 hover:border-[#10B981]/40"
                            }`}>
                      {c.name} ({c.contact_count})
                    </button>
                  ))}
                </div>
              )}
              {!form.all_contacts && lists.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  <Label className="text-xs text-slate-500">Oppure liste dinamiche</Label>
                  <div className="flex flex-wrap gap-2" data-testid="campaign-lists-dropdown">
                    {lists.map((l) => (
                      <button key={l.id} type="button" onClick={() => toggleList(l.id)}
                              data-testid={`campaign-list-${l.id}`}
                              className={`px-3 py-1.5 rounded-lg text-xs border transition-all ${
                                form.list_ids.includes(l.id)
                                  ? "bg-[#3B82F6] text-white border-[#3B82F6]"
                                  : "border-[#1F2937] text-slate-400 hover:border-[#3B82F6]/40"
                              }`}>
                        {l.name} ({l.contact_count})
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {estimate && (
                <div className="rounded-xl border border-[#1F2937] bg-[#0B1120] p-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" data-testid="campaign-cost-estimate">
                  <span className="text-slate-300 flex items-center gap-1"><Users2 className="h-3.5 w-3.5 text-[#3B82F6]" /> Destinatari: <b data-testid="campaign-estimate-count">{estimate.count}</b></span>
                  <span className="text-slate-300 flex items-center gap-1"><Euro className="h-3.5 w-3.5 text-[#10B981]" /> Costo previsto {estimate.cost.channel === "sms" ? "SMS" : "Meta"}: <b className="text-[#10B981]" data-testid="campaign-estimate-cost">~{fmtEur(estimate.cost.total)}</b></span>
                  <span className="text-slate-500" data-testid="campaign-estimate-source">
                    {estimate.count} × {fmtEur(estimate.cost.unit_cost, 4)} · {estimate.cost.channel === "sms" ? `${estimate.cost.segments} SMS per destinatario · ${estimate.cost.simulate ? "SIMULAZIONE attiva (nessun credito)" : "listino SMS"}` : estimate.cost.source === "meta" ? "tariffa reale Meta (ultimi 30 gg)" : "stima da listino"} · {estimate.cost.category}
                  </span>
                  {estimate.excluded?.total > 0 && (
                    <span className="text-[#EF4444] w-full flex items-center gap-1 flex-wrap" data-testid="campaign-estimate-excluded">
                      <Ban className="h-3.5 w-3.5" /> Esclusi {estimate.excluded.total}:
                      {estimate.excluded.unsubscribed > 0 && <span data-testid="campaign-estimate-excluded-unsubscribed">{estimate.excluded.unsubscribed} disiscritti (STOP)</span>}
                      {estimate.excluded.paused > 0 && <span>· {estimate.excluded.paused} in pausa</span>}
                      {estimate.excluded.undeliverable > 0 && <span>· {estimate.excluded.undeliverable} non raggiungibili</span>}
                      <Link to="/consensi?tab=unsubscribed" data-testid="campaign-estimate-excluded-link" className="underline hover:text-[#F87171]">Vedi la lista</Link>
                    </span>
                  )}
                  {estimate.cost.preferred_sms && (
                    <span className={`w-full ${estimate.cost.preferred_sms.blocked ? "text-[#EF4444]" : estimate.cost.preferred_sms.overridden ? "text-[#B45309]" : "text-[#1D4ED8]"}`} data-testid="campaign-estimate-preferred-sms">
                      {estimate.cost.preferred_sms.count} contatti preferiscono l'SMS: {estimate.cost.preferred_sms.blocked
                        ? `blocco — ${estimate.cost.preferred_sms.blocked}`
                        : estimate.cost.preferred_sms.overridden
                          ? `riceveranno comunque WhatsApp (preferenza ignorata, ${estimate.cost.preferred_sms.overridden} contatti)`
                          : estimate.cost.preferred_sms.will_sms
                            ? `riceveranno l'SMS (${estimate.cost.preferred_sms.segments} SMS × ${fmtEur(estimate.cost.preferred_sms.unit_cost, 4)} = ~${fmtEur(estimate.cost.preferred_sms.total)})`
                            : "verranno esclusi (scegli «Invia comunque via WhatsApp» o «Invia SMS» con un testo SMS per raggiungerli)"}
                    </span>
                  )}
                  {estimate.by_channel && (
                    <span className="w-full text-slate-300" data-testid="campaign-estimate-by-channel">
                      Destinatari per canale: WhatsApp {estimate.by_channel.whatsapp}{estimate.by_channel.whatsapp_overridden ? ` · WhatsApp con preferenza SMS ignorata ${estimate.by_channel.whatsapp_overridden}` : ""}{estimate.by_channel.sms ? ` · SMS ${estimate.by_channel.sms}` : ""}{estimate.by_channel.excluded_preference ? ` · esclusi per preferenza SMS ${estimate.by_channel.excluded_preference}` : ""}{estimate.by_channel.excluded_opposition ? ` (di cui ${estimate.by_channel.excluded_opposition} con opposizione a WhatsApp: mai forzati)` : ""}
                    </span>
                  )}
                  {estimate.cost.fallback && (
                    <span className="text-[#F59E0B] w-full" data-testid="campaign-estimate-fallback">
                      + fallback SMS stimato: ~{estimate.cost.fallback.count} numeri senza WhatsApp ({Math.round(estimate.cost.fallback.rate * 100)}% {estimate.cost.fallback.source}) × {fmtEur(estimate.cost.fallback.unit_cost, 4)} = ~{fmtEur(estimate.cost.fallback.total)} → totale ~{fmtEur(estimate.cost.total_with_fallback)}{estimate.cost.fallback.excluded ? ` · ${estimate.cost.fallback.excluded} numeri esclusi dal fallback (SMS già fallito negli ultimi 90 giorni)` : ""}
                    </span>
                  )}
                </div>
              )}
            </div>
            {/* Schedule */}
            <div className="space-y-2">
              <Label>Invio</Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="schedule-now-button"
                        onClick={() => { setBestApplied(false); setForm({ ...form, schedule_mode: "now" }); }}
                        className={`px-3 py-2 rounded-lg text-sm border transition-all ${
                          form.schedule_mode === "now" ? "bg-[#10B981] text-[#0A0E17] border-[#10B981]" : "border-[#1F2937] text-slate-400"
                        }`}>
                  <Send className="h-4 w-4 inline mr-1" /> Subito
                </button>
                <button type="button" data-testid="schedule-later-button"
                        onClick={() => setForm({ ...form, schedule_mode: "scheduled" })}
                        className={`px-3 py-2 rounded-lg text-sm border transition-all ${
                          form.schedule_mode === "scheduled" ? "bg-[#10B981] text-[#0A0E17] border-[#10B981]" : "border-[#1F2937] text-slate-400"
                        }`}>
                  <Clock className="h-4 w-4 inline mr-1" /> Programma
                </button>
              </div>
              <button type="button" data-testid="schedule-best-time-button" onClick={applyBestTime} disabled={!bestTime?.available}
                      className={`w-full px-3 py-2 rounded-lg text-sm border transition-all flex items-center justify-center gap-1.5 ${
                        bestTime?.available ? "border-[#F59E0B]/60 text-[#F59E0B] hover:bg-[#F59E0B]/10" : "border-[#1F2937] text-slate-600 cursor-not-allowed"
                      }`}>
                <Sparkles className="h-4 w-4" /> Programma all'orario migliore
                {bestTime?.available && <span className="text-xs text-slate-400">({DAYS_IT[bestTime.day]} {String(bestTime.hour).padStart(2, "0")}:00)</span>}
              </button>
              <p className="text-[11px] text-slate-500" data-testid="schedule-best-time-hint">
                {bestTime === null ? "Lettura del report orari in corso…"
                  : bestTime.available
                    ? (bestTime.scope === "segment"
                      ? `Calcolato sui clic dei destinatari selezionati (${bestTime.segment?.contacts} contatti, ${bestTime.clicks} clic in 90 giorni): picco ${DAYS_IT[bestTime.day]} ore ${String(bestTime.hour).padStart(2, "0")}:00.`
                      : `Suggerito dal report "Orario migliore": ${bestTime.clicks} clic negli ultimi 90 giorni, picco ${DAYS_IT[bestTime.day]} ore ${String(bestTime.hour).padStart(2, "0")}:00.${bestTime.segment ? ` I destinatari scelti hanno solo ${bestTime.segment.clicks} clic: uso il dato globale.` : ""}`)
                    : `Dati insufficienti: servono almeno ${bestTime.min_clicks} clic sui link negli ultimi 90 giorni (ora ${bestTime.clicks}). Il suggerimento si attiva da solo quando ci saranno abbastanza clic.`}
              </p>
              {form.schedule_mode === "scheduled" && (
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <Input type="datetime-local" data-testid="schedule-date-picker" value={form.scheduled_at}
                         onChange={(e) => { setBestApplied(false); setForm({ ...form, scheduled_at: e.target.value }); }}
                         className={`bg-[#0A0E17] ${bestApplied ? "border-[#F59E0B]" : "border-[#1F2937]"}`} />
                  <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                    <SelectTrigger className="bg-[#0A0E17] border-[#1F2937]" data-testid="recurrence-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                      <SelectItem value="none">Una volta</SelectItem>
                      <SelectItem value="daily">Ogni giorno</SelectItem>
                      <SelectItem value="weekly">Ogni settimana</SelectItem>
                      <SelectItem value="monthly">Ogni mese</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* Preview */}
          <div>
            <Label className="text-xs text-slate-500 mb-2 block">Anteprima WhatsApp</Label>
            <WhatsAppPreview body={fillVars(form.message_body)} imageUrl={form.image_url} />

            {isDemo ? (
              <div className="mt-4 space-y-1 bg-amber-50 border border-amber-300 rounded-xl p-3" data-testid="campaign-test-demo-notice">
                <Label className="text-xs text-amber-900">Messaggio di prova</Label>
                <p className="text-xs text-amber-900">Non disponibile nell'ambiente dimostrativo META REVIEW: nessun messaggio di prova viene inviato.</p>
              </div>
            ) : (
            <div className="mt-4 space-y-2 bg-[#0A0E17] border border-[#1F2937] rounded-xl p-3">
              <Label className="text-xs text-slate-400">Invia un messaggio di prova al tuo numero</Label>
              <div className="flex gap-2">
                <Input data-testid="campaign-test-phone" value={testPhone} onChange={(e) => setTestPhone(e.target.value)}
                       placeholder="+39 333 1234567" className="bg-[#111827] border-[#1F2937] h-9" />
                <Button type="button" data-testid="campaign-test-send" onClick={sendTest} disabled={testing}
                        className="bg-[#3B82F6] hover:bg-[#2563EB] text-white h-9 shrink-0">
                  {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Prova"}
                </Button>
              </div>
              <p className="text-xs text-slate-500">Questo numero sarà usato anche per il test automatico inviato il giorno prima delle campagne programmate.</p>
              {testResult && (
                <div data-testid="campaign-test-status" className="flex items-start gap-1.5 text-xs pt-1">
                  {testResult.state === "sent" && (
                    <><Check className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" /><span className="text-slate-400">Inviato, in attesa di consegna…</span></>
                  )}
                  {testResult.state === "delivered" && (
                    <><CheckCheck className="h-3.5 w-3.5 text-[#38BDF8] shrink-0 mt-0.5" /><span className="text-[#38BDF8]">Consegnato su WhatsApp</span></>
                  )}
                  {testResult.state === "read" && (
                    <><CheckCheck className="h-3.5 w-3.5 text-[#10B981] shrink-0 mt-0.5" /><span className="text-[#10B981]">Consegnato e letto</span></>
                  )}
                  {testResult.state === "demo" && (
                    <><CheckCircle2 className="h-3.5 w-3.5 text-slate-500 shrink-0 mt-0.5" /><span className="text-slate-500">Inviato in modalità demo (nessun invio reale)</span></>
                  )}
                  {testResult.state === "error" && (
                    <><XCircle className="h-3.5 w-3.5 text-[#EF4444] shrink-0 mt-0.5" /><span className="text-[#EF4444]">{testResult.msg}</span></>
                  )}
                </div>
              )}
            </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => save()} disabled={saving} data-testid="send-campaign-submit"
                  className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> :
              isDemo ? <><Save className="h-4 w-4 mr-2" /> Salva bozza (demo)</> :
              form.schedule_mode === "scheduled" ? <><Clock className="h-4 w-4 mr-2" /> Programma campagna</> :
              <><Send className="h-4 w-4 mr-2" /> Invia ora</>}
          </Button>
        </DialogFooter>
        <Dialog open={confirmUntested} onOpenChange={setConfirmUntested}>
          <DialogContent className="bg-[#111827] border-[#F59E0B]/50 text-slate-100 max-w-md" data-testid="untested-confirm-dialog">
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-[#F59E0B]" /> Hai fatto la prova sul tuo numero?</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-300">Stai per {form.schedule_mode === "scheduled" ? "programmare" : "inviare"} un messaggio <b>reale</b> con il template <span className="font-mono2 text-[#10B981]">{form.meta_template_name}</span> senza averlo ancora provato in questa sessione. Un invio di prova al tuo telefono costa quanto un singolo messaggio e ti evita di scoprire errori dopo l'invio a tutti.</p>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" onClick={() => setConfirmUntested(false)} data-testid="untested-go-test"
                      className="bg-[#1E293B] border-[#10B981]/40 text-[#10B981] hover:bg-[#334155]">
                <Smartphone className="h-4 w-4 mr-2" /> Faccio prima la prova
              </Button>
              <Button onClick={() => save(true)} disabled={saving} data-testid="untested-send-anyway"
                      className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0A0E17] font-semibold">
                <Send className="h-4 w-4 mr-2" /> {form.schedule_mode === "scheduled" ? "Programma comunque" : "Invia comunque"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
function ChainButton({ campaign }) {
  const [open, setOpen] = useState(false);
  const [chain, setChain] = useState(null);
  useEffect(() => {
    if (!open) return;
    setChain(null);
    api.get(`/campaigns/${campaign.id}/chain`).then((r) => setChain(r.data)).catch(() => toast.error("Catena non disponibile"));
  }, [open, campaign.id]);
  const STAGE = ["", "Primo invio", "Secondo invio", "Terzo invio"];
  const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`campaign-chain-${campaign.id}`} title="Catena promemoria" className="text-slate-500 hover:text-[#F59E0B] transition-colors">
          <GitBranch className="h-4 w-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0B1120] border-[#1F2937] text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Catena promemoria</DialogTitle></DialogHeader>
        {!chain ? <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#10B981]" /></div> : (
          <div className="space-y-0" data-testid="campaign-chain-body">
            {chain.nodes.map((n, i) => (
              <div key={n.stage} className="relative pl-8">
                {i < chain.nodes.length - 1 && <div className="absolute left-[11px] top-7 bottom-0 w-0.5 bg-[#1F2937]" />}
                <div className={`absolute left-0 top-1.5 h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold ${n.pending ? "bg-[#1F2937] text-slate-400" : n.id === chain.current_id ? "bg-[#F59E0B] text-[#0A0E17]" : "bg-[#10B981] text-[#0A0E17]"}`}>{n.stage}</div>
                <div className={`mb-4 rounded-xl border p-3 ${n.pending ? "border-dashed border-[#1F2937]" : "border-[#1F2937] bg-[#111827]"}`} data-testid={`chain-stage-${n.stage}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-sm font-medium text-slate-100">{STAGE[n.stage]}{n.name ? ` · ${n.name}` : ""}</p>
                    <span className="text-[11px] text-slate-500">{n.pending ? (n.skipped ? `Saltato: ${n.skipped}` : n.due_at ? `Programmato il ${fmtD(n.due_at)}` : `Dopo ${n.days} giorni`) : `Inviato il ${fmtD(n.sent_at)}`}</span>
                  </div>
                  {n.template && <p className="text-[11px] text-slate-500 mt-0.5">Template: {n.template}</p>}
                  {!n.pending && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-xs">
                      <div><p className="text-slate-500">Consegnati</p><p className="text-[#38BDF8] font-semibold">{n.delivered}<span className="text-slate-500 font-normal"> / {n.recipients}</span></p></div>
                      <div><p className="text-slate-500">Clic</p><p className="text-[#F59E0B] font-semibold">{n.click_contacts} <span className="text-slate-500 font-normal">({n.click_rate}%)</span></p></div>
                      <div><p className="text-slate-500">Senza clic</p><p className="text-slate-200 font-semibold">{n.not_clicked}</p></div>
                      <div><p className="text-slate-500">Costo</p><p className="text-[#10B981] font-semibold">{n.real_cost != null ? fmtEur(n.real_cost) : n.cost_estimate != null ? `~${fmtEur(n.cost_estimate)}` : "—"}</p></div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div className="rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-3 text-xs flex flex-wrap gap-x-4 gap-y-1" data-testid="chain-totals">
              <span className="text-slate-300">Totale consegnati: <b>{chain.totals.delivered}</b></span>
              <span className="text-slate-300">Contatti che hanno cliccato: <b className="text-[#F59E0B]">{chain.totals.click_contacts}</b></span>
              <span className="text-slate-300">Costo totale: <b className="text-[#10B981]">{chain.totals.real_cost != null ? fmtEur(chain.totals.real_cost) : chain.totals.cost_estimate != null ? `~${fmtEur(chain.totals.cost_estimate)}` : "—"}</b></span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}



function Stat({ label, value, color = "#E2E8F0" }) {
  return (
    <div className="bg-[#111827] border border-[#1F2937] rounded-lg p-3">
      <p className="text-[11px] text-slate-500">{label}</p>
      <p className="text-lg font-semibold" style={{ color }}>{value}</p>
    </div>
  );
}

function ClickStats({ clicks }) {
  const max = Math.max(1, ...clicks.by_day.map((d) => d.clicks));
  const fmtD = (d) => new Date(d + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
  const fmtT = (t) => (t ? new Date(t).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");
  return (
    <div className="rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-4 space-y-3" data-testid="campaign-click-stats">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-medium text-slate-200 flex items-center gap-2"><MousePointerClick className="h-4 w-4 text-[#F59E0B]" />Statistiche clic</p>
        <p className="text-[11px] text-slate-500">Primo clic {fmtT(clicks.first_click_at)} · ultimo {fmtT(clicks.last_click_at)}{clicks.anonymous ? ` · ${clicks.anonymous} anonimi` : ""}</p>
      </div>
      <div className="flex items-end gap-1.5 h-20" data-testid="click-by-day-chart">
        {clicks.by_day.map((d) => (
          <div key={d.day} className="flex-1 flex flex-col items-center gap-1 min-w-[28px]" title={`${fmtD(d.day)}: ${d.clicks} clic`}>
            <span className="text-[10px] text-[#F59E0B]">{d.clicks}</span>
            <div className="w-full rounded-t bg-[#F59E0B]/70" style={{ height: `${Math.max(4, (d.clicks / max) * 48)}px` }} />
            <span className="text-[9px] text-slate-500">{fmtD(d.day)}</span>
          </div>
        ))}
      </div>
      {clicks.by_link?.length > 0 && (
        <div className="space-y-1" data-testid="click-by-link">
          {clicks.by_link.map((l) => (
            <div key={l.code} className="flex items-center justify-between gap-3 text-xs">
              <span className="text-slate-300 truncate"><b className="text-slate-200">{l.label}</b> <span className="text-slate-500">{l.url}</span></span>
              <span className="text-[#F59E0B] shrink-0">{l.clicks} clic</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ButtonTapStats({ taps, onMakeList, busy }) {
  const max = Math.max(1, ...taps.by_button.map((b) => b.taps));
  return (
    <div className="rounded-xl border border-[#8B5CF6]/30 bg-[#8B5CF6]/5 p-4 space-y-3" data-testid="campaign-button-taps">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-medium text-slate-200 flex items-center gap-2"><MessageSquare className="h-4 w-4 text-[#8B5CF6]" />Pulsanti toccati (Testo / Disiscriviti)</p>
        <p className="text-[11px] text-slate-500">{taps.total} tocchi · {taps.unique_contacts} contatti{taps.last_tap_at ? ` · ultimo ${new Date(taps.last_tap_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}` : ""}</p>
      </div>
      <div className="space-y-1.5">
        {taps.by_button.map((b) => (
          <div key={b.text} className="space-y-0.5" data-testid={`button-tap-${b.text}`}>
            <div className="flex items-center justify-between gap-3 text-xs">
              <span className={`flex items-center gap-1.5 ${b.is_stop ? "text-[#FCA5A5]" : "text-slate-200"}`}>{b.is_stop ? <Ban className="h-3.5 w-3.5" /> : <MessageSquare className="h-3.5 w-3.5 text-[#8B5CF6]" />}<b>{b.text}</b>{b.is_stop && <span className="text-[10px] text-slate-500">disiscrizione</span>}</span>
              <span className="flex items-center gap-2 shrink-0">
                <span className={b.is_stop ? "text-[#FCA5A5]" : "text-[#8B5CF6]"}>{b.taps} tocchi · {b.contacts} contatti</span>
                {!b.is_stop && b.contacts > 0 && (
                  <button type="button" onClick={() => onMakeList(b.text)} disabled={!!busy} data-testid={`button-tap-list-${b.text}`}
                          className="rounded-md border border-[#8B5CF6]/50 px-2 py-0.5 text-[11px] text-[#C4B5FD] hover:bg-[#8B5CF6]/20 disabled:opacity-50 flex items-center gap-1">
                    {busy === b.text ? <Loader2 className="h-3 w-3 animate-spin" /> : <Users2 className="h-3 w-3" />} Crea lista e ricontatta
                  </button>
                )}
              </span>
            </div>
            <div className="h-1.5 rounded bg-[#1F2937]"><div className={`h-1.5 rounded ${b.is_stop ? "bg-[#EF4444]/70" : "bg-[#8B5CF6]/70"}`} style={{ width: `${Math.max(3, (b.taps / max) * 100)}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CampaignReportButton({ campaign, onRecontact }) {
  const [open, setOpen] = useState(false);
  const [rep, setRep] = useState(null);
  const [rows, setRows] = useState(null);
  const [diag, setDiag] = useState(null);
  const diagLoaded = useRef(false);
  const [recontacting, setRecontacting] = useState(false);
  const recontact = async (kind = "clicked") => {
    setRecontacting(kind);
    try {
      const { data } = await api.post(`/campaigns/${campaign.id}/recontact-list`, null, { params: { kind } });
      toast.success(`Lista "${data.name}" pronta (${data.contact_count} contatti)`);
      setOpen(false);
      onRecontact?.(data, kind);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore creazione lista"); }
    finally { setRecontacting(false); }
  };
  const [tapListBusy, setTapListBusy] = useState(null);
  const makeTapList = async (text) => {
    setTapListBusy(text);
    try {
      const { data } = await api.post(`/campaigns/${campaign.id}/tapped-list`, null, { params: { text } });
      toast.success(`Lista "${data.name}" pronta (${data.contact_count} contatti)`);
      setOpen(false);
      onRecontact?.(data, "tapped", text);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore creazione lista"); }
    finally { setTapListBusy(null); }
  };
  const [clicks, setClicks] = useState(null);
  const [taps, setTaps] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    if (!open) return;
    let active = true;
    const load = async () => {
      try {
        const [{ data }, rc] = await Promise.all([api.get(`/campaigns/${campaign.id}/report`), api.get(`/campaigns/${campaign.id}/recipients`)]);
        if (active) { setRep(data); setRows(rc.data.items || []); setClicks(rc.data.clicks || null); setTaps(rc.data.button_taps || null); }
        const zeroDeliv = data.is_real && (data.stats?.sent || 0) > 0 && (data.stats?.delivered || 0) === 0;
        const zeroClicks = data.is_real && (rc.data.clicks?.clicks || 0) === 0 && (data.stats?.sent || 0) > 0;
        if (active && (zeroDeliv || zeroClicks) && !diagLoaded.current) {
          diagLoaded.current = true;
          api.get(`/campaigns/${campaign.id}/diagnostics`).then((d) => active && setDiag(d.data)).catch(() => {});
        }
      } catch (e) { /* ignore */ }
    };
    load();
    const id = setInterval(load, 5000);
    return () => { active = false; clearInterval(id); };
  }, [open, campaign.id]);
  const isSms = rep?.channel === "sms";
  const so = rep?.sms_outcomes || null;
  const shown = (rows || []).filter((r) => filter === "all" ? true : filter === "delivered" ? ["delivered", "read"].includes(r.status) : filter === "clicked" ? !!r.clicked_at : filter === "not_clicked" ? (["delivered", "read"].includes(r.status) && !r.clicked_at) : filter === "sms" ? (r.channel !== "whatsapp" && r.channel !== "whatsapp_overridden") : filter === "overridden" ? r.channel === "whatsapp_overridden" : filter === "rejected" ? r.dlr === "REJECTED" : filter === "not_sent" ? r.dlr === "NOT_SENT" : filter === "not_delivered" ? (r.status === "failed" && r.dlr !== "NOT_SENT" && r.dlr !== "REJECTED") : filter === "pending" ? r.status === "sent" : r.status === filter);
  const EXPORTABLE = ["all", "delivered", "read", "failed", "rejected", "not_sent", "not_delivered", "pending", "sent"];
  const [exporting, setExporting] = useState(null);
  const exportRecipients = async (fmt) => {
    setExporting(fmt);
    try {
      const res = await api.get(`/campaigns/${campaign.id}/recipients/export?filter=${filter}&format=${fmt}`, { responseType: "blob" });
      const m = /filename="?([^";]+)"?/.exec(res.headers["content-disposition"] || "");
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : `destinatari_${filter}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`Export ${fmt.toUpperCase()} pronto (${shown.length} record, filtro «${filter}»)`);
    } catch (e) { toast.error(formatApiErrorDetail(e, "Esportazione non riuscita")); }
    finally { setExporting(null); }
  };
  const CHANNEL = {
    whatsapp: { label: "WhatsApp", cls: "bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30" },
    sms: { label: "SMS", cls: "bg-[#3B82F6]/15 text-[#60A5FA] border-[#3B82F6]/30" },
    sms_fallback: { label: "SMS fallback", cls: "bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/30" },
    sms_preferred: { label: "SMS (preferito)", cls: "bg-[#3B82F6]/15 text-[#93C5FD] border-[#3B82F6]/30" },
    whatsapp_overridden: { label: "WhatsApp (pref. SMS ignorata)", cls: "bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/30" },
    none: { label: "Escluso", cls: "bg-slate-700/40 text-slate-400 border-slate-600/40" },
  };
  const hasSms = (rows || []).some((r) => r.channel !== "whatsapp" && r.channel !== "whatsapp_overridden");
  const STATUS = {
    read: { label: "Letto", cls: "text-[#8B5CF6]", icon: CheckCheck },
    delivered: { label: "Consegnato", cls: "text-[#38BDF8]", icon: CheckCheck },
    sent: { label: "Inviato", cls: "text-slate-400", icon: Check },
    failed: { label: "Fallito", cls: "text-[#EF4444]", icon: XCircle },
    skipped: { label: "Escluso", cls: "text-slate-500", icon: XCircle },
  };
  const cnt = (fn) => (rows || []).filter(fn).length;
  const chips = isSms
    ? [["all", "Tutti", rows?.length], ["delivered", "Consegnati", cnt((r) => ["delivered", "read"].includes(r.status))], ...(cnt((r) => r.dlr === "REJECTED") ? [["rejected", "Respinti (VALIDATION)", cnt((r) => r.dlr === "REJECTED")]] : []), ["not_sent", "Rifiutati dal provider", cnt((r) => r.dlr === "NOT_SENT")], ["not_delivered", "Non consegnati", cnt((r) => r.status === "failed" && r.dlr !== "NOT_SENT" && r.dlr !== "REJECTED")], ["pending", "In attesa / senza esito", cnt((r) => r.status === "sent")]]
    : [["all", "Tutti", rows?.length], ["delivered", "Consegnati", cnt((r) => ["delivered", "read"].includes(r.status))], ["read", "Letti", cnt((r) => r.status === "read")], ["failed", "Falliti", cnt((r) => r.status === "failed")], ["sent", "Solo inviati", cnt((r) => r.status === "sent")], ["clicked", "Hanno cliccato", cnt((r) => r.clicked_at)], ["not_clicked", "Non hanno cliccato", cnt((r) => ["delivered", "read"].includes(r.status) && !r.clicked_at)], ...(hasSms ? [["sms", "Via SMS", cnt((r) => r.channel !== "whatsapp" && r.channel !== "whatsapp_overridden")]] : []), ...((rows || []).some((r) => r.channel === "whatsapp_overridden") ? [["overridden", "Pref. SMS ignorata", cnt((r) => r.channel === "whatsapp_overridden")]] : [])];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`campaign-report-${campaign.id}`} title="Report live"
                className="text-slate-500 hover:text-[#38BDF8] transition-colors">
          <Gauge className="h-4 w-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0B1120] border-[#1F2937] text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Report live · {campaign.name}</DialogTitle></DialogHeader>
        {!rep ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#10B981]" /></div>
        ) : (
          <div className="space-y-4" data-testid="campaign-report-body">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              {isSms && so ? (<>
              <Stat label="Destinatari" value={rep.recipients} />
              <Stat label="Accettati dal gateway" value={so.accepted} />
              <Stat label="Inoltrati dall'operatore" value={so.forwarded} color="#60A5FA" />
              <Stat label="Consegnati (DLR)" value={`${so.delivered} (${rep.delivery_rate}%)`} color="#38BDF8" />
              <div data-testid="sms-failed-stat">
                <Stat label="Falliti" value={so.failed} color="#EF4444" />
                <p className="text-[11px] text-slate-500 mt-1">{so.rejected ? <span data-testid="sms-rejected-stat">respinti prima dell'accettazione {so.rejected} · </span> : null}rifiutati dal provider {so.not_sent} · non consegnati {so.not_delivered}{so.failed_other ? ` · altri ${so.failed_other}` : ""}</p>
              </div>
              <div data-testid="sms-pending-stat">
                <Stat label="In attesa DLR" value={so.pending} color="#F59E0B" />
                {so.no_report > 0 && <p className="text-[11px] text-slate-500 mt-1">{so.no_report} senza esito definitivo (48 h)</p>}
              </div>
              <Stat label="Tipo" value={rep.is_real ? `Reale (${so.provider_label})` : "Demo (SMS simulati)"} color={rep.is_real ? "#10B981" : "#94A3B8"} />
              </>) : (<>
              <Stat label="Destinatari" value={rep.recipients} />
              <Stat label="Inviati" value={rep.sent} />
              <Stat label="Consegnati" value={`${rep.delivered} (${rep.delivery_rate}%)`} color="#38BDF8" />
              <Stat label="Letti" value={`${rep.read} (${rep.read_rate}%)`} color="#8B5CF6" />
              <Stat label="Falliti" value={rep.failed} color="#EF4444" />
              <Stat label="Clic sui link" value={clicks ? `${clicks.clicks}${clicks.unique_contacts ? ` · ${clicks.unique_contacts} contatti` : ""}${rep.delivered && clicks.unique_contacts ? ` (${Math.round(clicks.unique_contacts / rep.delivered * 100)}%)` : ""}` : "—"} color="#F59E0B" />
              <Stat label="Tipo" value={rep.is_real ? "Reale (Meta)" : "Demo"} color={rep.is_real ? "#10B981" : "#94A3B8"} />
              </>)}
              {rep.real_cost && (
                <Stat label="Costo reale Meta" color="#10B981" value={`${new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 4 }).format(rep.real_cost.total)} (${rep.real_cost.delivered} × ${rep.real_cost.unit_cost} €)`} />
              )}
              {!rep.cost_compare && rep.cost_estimate && (
                <Stat label="Costo previsto" color="#94A3B8" value={`${fmtEur(rep.cost_estimate.total, 2)} (${rep.cost_estimate.count} × ${rep.cost_estimate.unit_cost} €)`} />
              )}
              {rep.cost_compare && (
                <div data-testid="campaign-cost-compare">
                  <Stat label="Scostamento previsto / reale" color={rep.cost_compare.delta <= 0 ? "#10B981" : "#F59E0B"}
                        value={`${rep.cost_compare.delta > 0 ? "+" : ""}${fmtEur(rep.cost_compare.delta, 2)}${rep.cost_compare.delta_pct !== null ? ` (${rep.cost_compare.delta_pct > 0 ? "+" : ""}${rep.cost_compare.delta_pct}%)` : ""}`} />
                  <p className="text-[11px] text-slate-500 mt-1" data-testid="campaign-cost-compare-detail">
                    Previsto {fmtEur(rep.cost_compare.forecast, 2)} ({rep.cost_compare.forecast_count} × {rep.cost_compare.forecast_unit} €, {rep.cost_compare.forecast_source === "meta" ? "tariffa reale" : rep.cost_compare.forecast_source}) · Reale {fmtEur(rep.cost_compare.real, 2)}
                    {rep.cost_compare.delta < 0 ? " · hai speso meno del previsto (messaggi non consegnati non si pagano)" : rep.cost_compare.delta > 0 ? " · tariffa Meta più alta del previsto" : " · in linea con la previsione"}
                  </p>
                </div>
              )}
              {rep.resend && (
                <Stat label="Reinvio a chi non clicca" color={rep.resend.done_at ? "#10B981" : "#F59E0B"}
                      value={rep.resend.done_at
                        ? (rep.resend.campaign_id ? `Effettuato il ${new Date(rep.resend.done_at).toLocaleDateString("it-IT")}` : `Saltato: ${rep.resend.skipped || "nessun destinatario"}`)
                        : rep.resend.due_at ? `Programmato il ${new Date(rep.resend.due_at).toLocaleDateString("it-IT")}` : `Dopo ${rep.resend.days} giorni dall'invio`} />
              )}
              {(rep.channel === "sms" || rep.sms?.sent > 0 || rep.sms?.failed > 0 || rep.sms?.channel_skipped > 0 || rep.sms?.overridden > 0) && (
                <div data-testid="campaign-sms-stats">
                  <Stat label={rep.channel === "sms" ? `Canale ${so?.provider_label || "SMS"}` : rep.sms?.overridden && !rep.sms?.sent && !rep.sms?.failed ? "Preferenza canale" : "Fallback SMS"} color="#3B82F6"
                        value={isSms && so ? `${so.accepted} accettati · ${so.forwarded} inoltrati · ${so.delivered} consegnati · ${so.failed} falliti · ${so.pending} in attesa${rep.sms.simulated ? ` · ${rep.sms.simulated} simulati` : ""}` : `${rep.sms.sent} inviati · ${rep.sms.delivered} consegnati · ${rep.sms.failed} falliti${rep.sms.fallback && rep.channel !== "sms" ? ` · ${rep.sms.fallback} via fallback` : ""}${rep.sms.fallback_skipped ? ` · ${rep.sms.fallback_skipped} non ritentati` : ""}${rep.sms.preferred ? ` · ${rep.sms.preferred} per preferenza SMS` : ""}${rep.sms.channel_skipped ? ` · ${rep.sms.channel_skipped} esclusi (preferiscono SMS, nessun testo SMS)` : ""}${rep.sms.overridden ? ` · ${rep.sms.overridden} WhatsApp con preferenza SMS ignorata` : ""}${rep.sms.simulated ? ` · ${rep.sms.simulated} simulati` : ""}`} />
                  <p className="text-[11px] text-slate-500 mt-1">{so?.provider === "2ck"
                    ? `Esiti per destinatario letti da ForYou/2CK (riconciliazione automatica ogni 15 min)${so.foryou_ids?.length ? ` · ID ForYou ${so.foryou_ids.join(", ")}` : ""}.`
                    : `Gli esiti «consegnato/fallito» degli SMS arrivano dal webhook DLR di SMSFactor (Integrazioni → Canale SMS → Registra webhook esiti).${rep.sms.fallback_skipped ? " I numeri con un SMS già fallito negli ultimi 90 giorni non vengono ritentati via SMS per non sprecare crediti." : ""}`}</p>
                </div>
              )}
              {rep.per_contact_timing && (
                <Stat label="Orario per contatto" color="#F59E0B"
                      value={rep.timing_deferred ? `${rep.timing_deferred} contatti in attesa della loro fascia oraria` : `Attivo · ${rep.timing_waves || 1} ondat${(rep.timing_waves || 1) === 1 ? "a" : "e"} di invio`} />
              )}
              {rep.resend2 && (
                <Stat label="Terzo promemoria" color={rep.resend2.done_at ? "#10B981" : "#F59E0B"}
                      value={rep.resend2.done_at
                        ? (rep.resend2.campaign_id ? `Effettuato il ${new Date(rep.resend2.done_at).toLocaleDateString("it-IT")}` : `Saltato: ${rep.resend2.skipped || "nessun destinatario"}`)
                        : rep.resend2.due_at ? `Programmato il ${new Date(rep.resend2.due_at).toLocaleDateString("it-IT")}` : `Dopo ${rep.resend2.days} giorni dal secondo invio`} />
              )}
              {!isSms && clicks && clicks.delivered_contacts > 0 && (
                <Stat label="Consegnati senza clic" value={`${clicks.delivered_not_clicked} (${Math.round(clicks.delivered_not_clicked / clicks.delivered_contacts * 100)}%)`} color="#94A3B8" />
              )}
            </div>
            {!isSms && clicks && (clicks.unique_contacts > 0 || clicks.delivered_not_clicked > 0 || (rep.failed > 0 && rep.channel !== "sms")) && (
              <div className="flex flex-wrap gap-2">
                {rep.failed > 0 && rep.channel !== "sms" && (
                  <Button type="button" size="sm" variant="outline" onClick={() => recontact("undelivered")} disabled={!!recontacting} data-testid="resend-sms-undelivered-button"
                          className="bg-[#1E293B] border-[#3B82F6]/40 text-[#93C5FD] hover:bg-[#334155]">
                    {recontacting === "undelivered" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                    Reinvia via SMS ai non consegnati ({rep.failed})
                  </Button>
                )}
                {clicks.unique_contacts > 0 && (
                  <Button type="button" size="sm" onClick={() => recontact("clicked")} disabled={!!recontacting} data-testid="recontact-clickers-button"
                          className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0A0E17] font-semibold">
                    {recontacting === "clicked" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MousePointerClick className="h-4 w-4 mr-2" />}
                    Ricontatta chi ha cliccato ({clicks.unique_contacts})
                  </Button>
                )}
                {clicks.delivered_not_clicked > 0 && (
                  <Button type="button" size="sm" variant="outline" onClick={() => recontact("not_clicked")} disabled={!!recontacting} data-testid="recontact-nonclickers-button"
                          className="bg-[#1E293B] border-[#94A3B8]/30 text-slate-200 hover:bg-[#334155]">
                    {recontacting === "not_clicked" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                    Ricontatta chi non ha cliccato ({clicks.delivered_not_clicked})
                  </Button>
                )}
              </div>
            )}
            <p className="text-xs text-slate-500" data-testid="campaign-report-footnote">
              {isSms && so
                ? `${rep.is_real ? `Esiti dal provider (${so.provider_label}), aggiornati dalla riconciliazione automatica ogni 15 min` : "Campagna SMS simulata: nessun invio reale"} · ultimo esito ricevuto: ${so.last_dlr_at ? new Date(so.last_dlr_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "nessuno"}.`
                : rep.is_real ? "Aggiornamento automatico ogni 5s dai webhook Meta." : "Campagna demo: statistiche simulate."}
            </p>
            {isSms && so && so.unreconciled?.length > 0 && (
              <p className="text-xs text-[#F59E0B] flex items-center gap-1" data-testid="sms-unreconciled-warning"><AlertTriangle className="h-3.5 w-3.5" />{so.unreconciled.length} blocc{so.unreconciled.length === 1 ? "o" : "hi"} non ancora riconciliat{so.unreconciled.length === 1 ? "o" : "i"} con il provider: i conteggi possono cambiare al prossimo giro.</p>
            )}
            {!isSms && clicks && clicks.clicks > 0 && <ClickStats clicks={clicks} />}
            {taps && taps.total > 0 && <ButtonTapStats taps={taps} onMakeList={makeTapList} busy={tapListBusy} />}
            {diag && diag.problems?.length > 0 && (
              <div className="rounded-xl border border-[#EF4444]/40 bg-[#EF4444]/10 p-4 space-y-2" data-testid="campaign-diagnostics">
                <p className="text-sm font-medium text-[#FCA5A5] flex items-center gap-2"><AlertTriangle className="h-4 w-4" />Perché le statistiche sono a zero</p>
                <ul className="list-disc pl-5 space-y-1 text-xs text-slate-300">
                  {diag.problems.map((p, i) => <li key={i} data-testid={`campaign-diag-${i}`}>{p}</li>)}
                </ul>
                <p className="text-[11px] text-slate-500">Messaggi: {diag.messages} · con ID Meta: {diag.with_wamid} · aggiornamenti di stato ricevuti: {diag.status_updates}{diag.tracking?.link_tracking ? ` · tracciamento template: ${diag.tracking.link_tracking}` : ""}</p>
              </div>
            )}
            <div className="space-y-2" data-testid="campaign-recipients-section">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm font-medium text-slate-200">Dettaglio per destinatario</p>
                <div className="flex flex-wrap gap-1.5 items-center">
                  {chips.map(([k, l, n]) => (
                    <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`recipients-filter-${k}`}
                            className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${filter === k ? "bg-[#10B981]/15 border-[#10B981]/40 text-[#10B981]" : "border-[#1F2937] text-slate-400 hover:text-slate-200"}`}>
                      {l}{typeof n === "number" ? ` · ${n}` : ""}
                    </button>
                  ))}
                  {rep.can_export && EXPORTABLE.includes(filter) && (
                    <span className="inline-flex gap-1 ml-1" data-testid="recipients-export-group">
                      {["csv", "xlsx"].map((fmt) => (
                        <button key={fmt} type="button" onClick={() => exportRecipients(fmt)} disabled={!!exporting || !shown.length} data-testid={`recipients-export-${fmt}`}
                                title={`Esporta i ${shown.length} record del filtro attivo (${fmt.toUpperCase()})`}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border border-[#3B82F6]/60 text-[#1D4ED8] bg-[#3B82F6]/10 hover:bg-[#3B82F6]/20 disabled:opacity-50 transition-colors">
                          {exporting === fmt ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}{fmt.toUpperCase()}
                        </button>
                      ))}
                    </span>
                  )}
                </div>
              </div>
              <div className="rounded-xl border border-[#1F2937] overflow-hidden">
                <div className="max-h-[320px] overflow-y-auto">
                  {!rows ? (
                    <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#10B981]" /></div>
                  ) : shown.length === 0 ? (
                    <p className="text-xs text-slate-500 text-center py-6" data-testid="recipients-empty">Nessun destinatario in questo stato.</p>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-[#111827] text-slate-500">
                        <tr><th className="text-left px-3 py-2 font-medium">Contatto</th><th className="text-left px-3 py-2 font-medium">Telefono</th><th className="text-left px-3 py-2 font-medium">Canale</th><th className="text-left px-3 py-2 font-medium">Stato</th><th className="text-left px-3 py-2 font-medium hidden sm:table-cell">Quando</th><th className="text-left px-3 py-2 font-medium hidden sm:table-cell">Clic link</th></tr>
                      </thead>
                      <tbody>
                        {shown.map((r, i) => {
                          const s = STATUS[r.status] || STATUS.sent; const Icon = s.icon; const ch = CHANNEL[r.channel] || CHANNEL.whatsapp;
                          return (
                            <tr key={r.id || r.wamid || `${r.phone}-${i}`} className="border-t border-[#1F2937]" data-testid={`recipient-row-${i}`}>
                              <td className="px-3 py-2 text-slate-200">{r.name}</td>
                              <td className="px-3 py-2 font-mono2 text-slate-400">{r.phone}</td>
                              <td className="px-3 py-2" data-testid={`recipient-channel-${i}`}>
                                <span className={`inline-block px-1.5 py-0.5 rounded border text-[10px] ${ch.cls}`}>{ch.label}{r.simulated ? " (sim.)" : ""}</span>
                              </td>
                              <td className={`px-3 py-2 ${s.cls}`}>
                                <span className="inline-flex items-center gap-1"><Icon className="h-3.5 w-3.5" /> {r.channel !== "whatsapp" && r.dlr_label ? r.dlr_label : s.label}</span>
                                {r.error && r.error !== r.dlr_label && <p className="text-[10px] text-[#EF4444]/80 mt-0.5 break-all" data-testid={`recipient-error-${i}`}>{r.error}</p>}
                                {r.fallback_skipped && <p className="text-[10px] text-[#F59E0B]/80 mt-0.5" data-testid={`recipient-fallback-skipped-${i}`}>SMS non ritentato: già fallito negli ultimi 90 giorni</p>}
                              </td>
                              <td className="px-3 py-2 text-slate-500 hidden sm:table-cell">{(r.sms_dlr_at || r.sent_at) ? new Date(r.sms_dlr_at || r.sent_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
                              <td className="px-3 py-2 hidden sm:table-cell" data-testid={`recipient-click-${i}`}>{r.clicked_at ? <span className="inline-flex items-center gap-1 text-[#F59E0B]"><MousePointerClick className="h-3.5 w-3.5" /> {new Date(r.clicked_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</span> : <span className="text-slate-600">—</span>}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
